"use client"

/*
==================================================
CART MOBILE ITEMS OVERLAY

Функция:
единый overlay списка товаров корзины
с разным поведением для mobile и desktop

Родитель:
CartDropdown / index.tsx

Дочерние элементы:
ItemsPreviewTemplate

Использует:
mobile fullscreen overlay + desktop compact overlay

Примечания:
- mobile: возвращено закрытие в стиле оригинала
- desktop: сохранён крестик возле счётчика
- для списка товаров возвращены более свободные
  внутренние отступы и более широкая рамка
==================================================
*/

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useRouter } from "next/navigation"
import { HttpTypes } from "@medusajs/types"
import ItemsPreviewTemplate from "@modules/cart/templates/preview"

type CartMobileItemsOverlayProps = {
  open: boolean
  cart: HttpTypes.StoreCart | null
  onClose: () => void
}

export default function CartMobileItemsOverlay({
  open,
  cart,
  onClose,
}: CartMobileItemsOverlayProps) {
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const router = useRouter()

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!open) {
      setVisible(false)
      return
    }

    const prevHtmlOverflow = document.documentElement.style.overflow
    const prevBodyOverflow = document.body.style.overflow

    document.documentElement.style.overflow = "hidden"
    document.body.style.overflow = "hidden"

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }

    document.addEventListener("keydown", handleKeyDown)

    const first = requestAnimationFrame(() => {
      const second = requestAnimationFrame(() => {
        setVisible(true)
      })
      ;(window as Window & { __cartOverlayRaf2?: number }).__cartOverlayRaf2 = second
    })

    return () => {
      cancelAnimationFrame(first)
      const second = (window as Window & { __cartOverlayRaf2?: number }).__cartOverlayRaf2
      if (second) cancelAnimationFrame(second)
      document.documentElement.style.overflow = prevHtmlOverflow
      document.body.style.overflow = prevBodyOverflow
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, onClose])

  if (!mounted || !open || !cart) return null

  const itemsCount =
    cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0

  const hasItems = itemsCount > 0

  return createPortal(
    <div
      className={[
        "fixed inset-0 z-[9998]",
        "bg-gradient-to-b from-slate-950/96 via-slate-900/96 to-slate-950/98 md:bg-black/10",
        "transition-opacity duration-300 md:duration-500",
        visible ? "opacity-100" : "opacity-0",
      ].join(" ")}
      onClick={onClose}
    >
      {/* MOBILE VERSION */}
      <div className="mx-auto flex h-[100dvh] w-full max-w-xl flex-col px-0 py-3 sm:py-4 md:hidden">
        <div
          className={[
            "flex h-full min-h-0 w-full flex-col overflow-hidden",
            "rounded-[30px] border border-white/10",
            "bg-gradient-to-b from-white via-white to-slate-50",
            "shadow-[0_26px_70px_rgba(0,0,0,0.38),0_12px_30px_rgba(15,23,42,0.18)]",
            "will-change-transform transition-all duration-500",
            visible
              ? "translate-y-0 scale-100 opacity-100"
              : "translate-y-8 scale-[0.92] opacity-70",
          ].join(" ")}
          style={{
            transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-50 h-0 w-full">
            <button
              type="button"
              onClick={onClose}
              className="
                absolute right-6 top-28
                flex h-7 w-7 items-center justify-center
                rounded-md
                border border-white/90
                bg-[#0cad03]
                shadow-[0_4px_4px_rgba(0,0,0,0.2),0_4px_4px_rgba(0,0,0,0.15)]
                transition-all duration-200
                active:scale-90 active:shadow-sm
              "
              aria-label="Close overlay"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 text-white"
              >
                <path d="M4 14h6v6" />
                <path d="M20 10h-6V4" />
                <path d="M14 10l7-7" />
                <path d="M10 14l-7 7" />
              </svg>
            </button>
          </div>

          <div className="shrink-0 px-3 pt-3">
            <div
              className="
                rounded-[22px] border border-slate-200
                bg-gradient-to-r from-slate-50 via-white to-slate-100
                px-4 py-3
                shadow-[inset_0_1px_0_rgba(255,255,255,0.92),0_6px_14px_rgba(15,23,42,0.08)]
              "
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-extrabold uppercase tracking-[0.1em] text-slate-800">
                    Cart items
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Scroll to view all products
                  </div>
                </div>

                <div
                  className="
                    inline-flex shrink-0 items-center justify-center
                    rounded-full border border-[#1e3a5f]/20
                    bg-gradient-to-b from-[#183b63] to-[#0f2742]
                    px-3 py-1
                    text-sm font-extrabold text-white
                    shadow-[0_6px_14px_rgba(15,23,42,0.22)]
                  "
                >
                  {itemsCount}
                </div>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-3">
            <div
              className="
                rounded-[24px] border border-slate-200
                bg-white
                shadow-[0_12px_28px_rgba(15,23,42,0.10)]
              "
            >
              <div className="px-4 py-3 sm:px-5 sm:py-4">
                <ItemsPreviewTemplate cart={cart} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP VERSION */}
      <div
        className={[
          "hidden md:block",
          "absolute right-4 top-[74px] w-[460px] max-w-[calc(100vw-32px)]",
          "overflow-hidden rounded-[28px] border border-white/20",
          "bg-gradient-to-b from-white via-white to-slate-50",
          "shadow-[0_30px_80px_rgba(15,23,42,0.28),0_14px_34px_rgba(0,0,0,0.18)]",
          "transition-all duration-300 will-change-transform",
          visible
            ? "translate-y-0 scale-100 opacity-100"
            : "translate-y-3 scale-[0.97] opacity-0",
        ].join(" ")}
        style={{
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {hasItems ? (
          <>
            <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-100 px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-extrabold uppercase tracking-[0.1em] text-slate-800">
                    Cart items
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Quick desktop preview before checkout
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div
                    className="
                      inline-flex shrink-0 items-center justify-center
                      rounded-full border border-[#1e3a5f]/20
                      bg-gradient-to-b from-[#183b63] to-[#0f2742]
                      px-3 py-1
                      text-sm font-extrabold text-white
                      shadow-[0_6px_14px_rgba(15,23,42,0.22)]
                    "
                  >
                    {itemsCount}
                  </div>

                  <button
                    type="button"
                    onClick={onClose}
                    className="
                      flex h-8 w-8 items-center justify-center
                      rounded-full border border-slate-200
                      bg-white text-slate-600
                      shadow-[0_4px_10px_rgba(15,23,42,0.08)]
                      transition hover:scale-105 hover:text-slate-900
                    "
                    aria-label="Close overlay"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            <div className="max-h-[55vh] overflow-y-auto px-3 py-3">
              <div
                className="
                  rounded-[24px] border border-slate-200
                  bg-white
                  shadow-[0_12px_28px_rgba(15,23,42,0.10)]
                "
              >
                <div className="px-4 py-3">
                  <ItemsPreviewTemplate cart={cart} />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 bg-white px-4 py-4">
              <button
                type="button"
                onClick={() => {
                  onClose()
                  router.push("/checkout")
                }}
                className="
                  w-full rounded-2xl
                  bg-gradient-to-r from-green-700 to-green-600
                  px-4 py-3
                  text-sm font-extrabold uppercase tracking-wide text-white
                  shadow-[0_10px_20px_rgba(0,0,0,0.22)]
                  transition-all duration-300
                  hover:-translate-y-0.5 hover:brightness-110
                  active:scale-[0.98]
                "
              >
                Checkout
              </button>
            </div>
          </>
        ) : (
          <div className="px-6 py-8">
            <div className="flex items-start justify-end">
              <button
                type="button"
                onClick={onClose}
                className="
                  flex h-8 w-8 items-center justify-center
                  rounded-full border border-slate-200
                  bg-white text-slate-600
                  shadow-[0_4px_10px_rgba(15,23,42,0.08)]
                  transition hover:scale-105 hover:text-slate-900
                "
                aria-label="Close overlay"
              >
                ×
              </button>
            </div>

            <div className="flex min-h-[180px] items-center justify-center">
              <div className="text-center">
                <div className="text-base font-extrabold uppercase tracking-[0.08em] text-slate-700">
                  Cart is empty
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Add products to see them here
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
